import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accessibility,
  Bus,
  GraduationCap,
  IdCard,
  Link2,
  MessageSquare,
  Save,
  ShieldCheck,
  UserCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import { obterCondutorPorSlug } from '@/modulos/operacao/condutores/dados';
import { obterMonitorPorSlug } from '@/modulos/operacao/monitores/dados';
import { obterVeiculoPorSlug } from '@/modulos/operacao/veiculos/dados';
import {
  AVISO_DE_CADASTRO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { OPCOES_DE_SITUACAO_DA_PESSOA } from '@/modulos/operacao/comum/contrato';
import { mascaraCpf, mascaraPlaca, somenteDigitos } from '@/nucleo/formato/documentos';
import { ComoFuncionaAIntegracao } from '@/modulos/admin/catalogo-veiculos/ComoFuncionaAIntegracao';
import { FormBackButton } from '@/ui/FormBackButton';
import { FormSection } from '@/ui/FormSection';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Switch } from '@/ui/base/switch';
import { Textarea } from '@/ui/base/textarea';
import { Card, CardContent } from '@/ui/base/card';
import { ErrorState } from '@/ui/ErrorState';

import type { TipoDeCadastro } from './CadastroDetalhePage';

type RegistroEditavel = Record<string, string | number | boolean | null | undefined> & {
  nome?: string;
  placa?: string;
  credenciados?: { nome?: string } | null;
};

function paraDataInput(valor: unknown): string {
  if (!valor) return '';
  const data = new Date(String(valor));
  return Number.isNaN(data.getTime())
    ? String(valor).slice(0, 10)
    : data.toISOString().slice(0, 10);
}

function CamposData({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input type="date" value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function CampoTexto({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  mono = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className={mono ? 'font-mono' : undefined}
      />
    </div>
  );
}

function CampoBooleano({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-border/70 bg-muted/20 p-4">
      <Label className="cursor-pointer">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

export function CadastroEdicaoPage({ tipo }: { tipo: Exclude<TipoDeCadastro, 'titulares'> }) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const registro = useMemo(() => {
    if (tipo === 'condutores') return obterCondutorPorSlug(slug);
    if (tipo === 'monitores') return obterMonitorPorSlug(slug);
    return obterVeiculoPorSlug(slug);
  }, [tipo, slug]);
  const [valores, setValores] = useState<Record<string, string | boolean>>({});

  useEffect(() => {
    setCarregando(true);
    setErro(false);
    const timer = window.setTimeout(() => {
      if (!registro) {
        setErro(true);
      } else {
        const item = registro as unknown as RegistroEditavel;
        const campos = Object.fromEntries(
          Object.entries(item).map(([chave, valor]) => [
            chave,
            typeof valor === 'boolean' ? valor : valor == null ? '' : String(valor),
          ]),
        );
        for (const chave of [
          'data_nascimento',
          'cnh_validade',
          'crmc_validade',
          'curso_primeiro_socorros_validade',
          'curso_transporte_escolar_validade',
          'curso_mobilidade_validade',
          'data_vencimento_atestado_medico',
          'certidao_criminal_emissao',
          'crlv_validade',
          'crm_pmsp_validade',
          'vistoria_validade',
          'tacografo_validade',
          'seguro_validade',
        ]) {
          if (campos[chave]) campos[chave] = paraDataInput(campos[chave]);
        }
        setValores(campos);
      }
      setCarregando(false);
    }, 120);
    return () => window.clearTimeout(timer);
  }, [registro]);

  function definir(chave: string, valor: string | boolean) {
    setValores((anterior) => ({ ...anterior, [chave]: valor }));
    setEnviado(false);
  }

  function texto(chave: string): string {
    const valor = valores[chave];
    return typeof valor === 'boolean' || valor == null ? '' : valor;
  }

  function booleano(chave: string): boolean {
    return valores[chave] === true || valores[chave] === 'true';
  }

  function submeter(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const obrigatorios: Array<[string, string]> =
      tipo === 'veiculos'
        ? [
            ['placa', 'Placa'],
            ['marca', 'Marca'],
            ['modelo', 'Modelo'],
          ]
        : [
            ['cpf', 'CPF'],
            ['nome', 'Nome'],
          ];
    const faltante = obrigatorios.find(([chave]) => !texto(chave).trim());
    if (faltante) {
      setEnviado(true);
      toast.error('Revise os campos obrigatórios', {
        description: `${faltante[1]} precisa ser preenchido.`,
      });
      return;
    }
    mostrarAviso(AVISO_DE_CADASTRO_INDISPONIVEL);
  }

  if (carregando) {
    return (
      <div className="space-y-4" aria-busy="true" aria-label="Carregando formulário">
        <div className="h-10 w-56 animate-pulse rounded-lg bg-muted" />
        <div className="h-128 animate-pulse rounded-xl bg-muted" />
      </div>
    );
  }

  if (erro || !registro) {
    const falha = (
      <ErrorState
        variant="load-failed"
        description="Não foi possível carregar o registro para edição. Volte para a listagem e tente novamente."
        primaryAction={{
          label: 'Voltar para a listagem',
          onClick: () => navigate(`/operacao/${tipo}`),
        }}
      />
    );
    if (tipo !== 'veiculos') return falha;
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <ComoFuncionaAIntegracao />
        </div>
        {falha}
      </div>
    );
  }

  const item = registro as unknown as RegistroEditavel;
  const nome = tipo === 'veiculos' ? mascaraPlaca(item.placa) : item.nome;
  const lista = `/operacao/${tipo}`;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FormBackButton to={`/operacao/${tipo}/${item.slug}`} label="Detalhe" />
        <p className="text-sm text-muted-foreground">
          Edição de{' '}
          {tipo === 'veiculos' ? 'veículo' : tipo === 'condutores' ? 'condutor' : 'monitor'}
        </p>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Editar {nome}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Atualize os campos conforme o padrão do cadastro TEG.
          </p>
        </div>
        {tipo === 'veiculos' && <ComoFuncionaAIntegracao className="shrink-0" />}
      </div>

      <Alert className="border-primary/30 bg-primary/5">
        <ShieldCheck className="h-4 w-4 text-primary" />
        <AlertDescription>
          Esta tela reproduz o formulário do protótipo, mas o cadastro continua sem persistência
          porque a API de escrita ainda não existe. O botão Salvar valida os campos e mostra um
          aviso honesto; não inventa uma gravação.
        </AlertDescription>
      </Alert>

      <form className="space-y-6" onSubmit={submeter} noValidate>
        {tipo !== 'veiculos' ? (
          <>
            <FormSection
              title="Dados pessoais"
              subtitle="Identificação do cadastro"
              icon={UserCircle}
            >
              <div className="grid gap-5 sm:grid-cols-3">
                <CampoTexto
                  label="CPF"
                  value={mascaraCpf(texto('cpf'))}
                  mono
                  onChange={(valor) => definir('cpf', somenteDigitos(valor))}
                />
                <div className="sm:col-span-2">
                  <CampoTexto
                    label="Nome"
                    value={texto('nome')}
                    onChange={(valor) => definir('nome', valor)}
                  />
                </div>
                <CampoTexto
                  label="RG"
                  value={texto('rg')}
                  onChange={(valor) => definir('rg', valor)}
                />
                <CamposData
                  label="Data de nascimento"
                  value={texto('data_nascimento')}
                  onChange={(valor) => definir('data_nascimento', valor)}
                />
                <div className="space-y-1">
                  <Label>Situação</Label>
                  <Select
                    value={texto('status') || 'ATIVO'}
                    onValueChange={(valor) => definir('status', valor)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OPCOES_DE_SITUACAO_DA_PESSOA.map((opcao) => (
                        <SelectItem key={opcao.value} value={opcao.value}>
                          {opcao.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </FormSection>

            {tipo === 'condutores' && (
              <FormSection
                title="Habilitação"
                subtitle="CNH e Cadastro Municipal de Condutor"
                icon={IdCard}
              >
                <div className="grid gap-5 sm:grid-cols-3">
                  <CampoTexto
                    label="CNH — número"
                    value={texto('cnh_numero')}
                    onChange={(valor) => definir('cnh_numero', valor)}
                    mono
                  />
                  <CampoTexto
                    label="CNH — categoria"
                    value={texto('cnh_categoria')}
                    onChange={(valor) => definir('cnh_categoria', valor.toUpperCase())}
                  />
                  <CamposData
                    label="CNH — validade"
                    value={texto('cnh_validade')}
                    onChange={(valor) => definir('cnh_validade', valor)}
                  />
                  <CampoTexto
                    label="CRMC — número"
                    value={texto('crmc_numero')}
                    onChange={(valor) => definir('crmc_numero', valor)}
                    mono
                  />
                  <CamposData
                    label="CRMC — validade"
                    value={texto('crmc_validade')}
                    onChange={(valor) => definir('crmc_validade', valor)}
                  />
                </div>
              </FormSection>
            )}

            <FormSection
              title="Cursos obrigatórios"
              subtitle="Capacitações e documentos de habilitação"
              icon={GraduationCap}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <CampoBooleano
                  label="Curso de primeiros socorros"
                  checked={booleano('curso_primeiro_socorros')}
                  onChange={(valor) => definir('curso_primeiro_socorros', valor)}
                />
                <CamposData
                  label="Validade — primeiros socorros"
                  value={texto('curso_primeiro_socorros_validade')}
                  onChange={(valor) => definir('curso_primeiro_socorros_validade', valor)}
                />
                <CampoBooleano
                  label="Curso de transporte escolar"
                  checked={booleano('curso_transporte_escolar')}
                  onChange={(valor) => definir('curso_transporte_escolar', valor)}
                />
                <CamposData
                  label="Validade — transporte escolar"
                  value={texto('curso_transporte_escolar_validade')}
                  onChange={(valor) => definir('curso_transporte_escolar_validade', valor)}
                />
                {tipo === 'condutores' && (
                  <>
                    <CampoBooleano
                      label="Curso de mobilidade reduzida"
                      checked={booleano('curso_mobilidade_reduzida')}
                      onChange={(valor) => definir('curso_mobilidade_reduzida', valor)}
                    />
                    <CamposData
                      label="Validade — mobilidade reduzida"
                      value={texto('curso_mobilidade_validade')}
                      onChange={(valor) => definir('curso_mobilidade_validade', valor)}
                    />
                  </>
                )}
                <CamposData
                  label="Atestado médico — vencimento"
                  value={texto('data_vencimento_atestado_medico')}
                  onChange={(valor) => definir('data_vencimento_atestado_medico', valor)}
                />
                <CamposData
                  label="Certidão criminal — emissão"
                  value={texto('certidao_criminal_emissao')}
                  onChange={(valor) => definir('certidao_criminal_emissao', valor)}
                />
              </div>
            </FormSection>

            <FormSection title="Vínculo" subtitle="Credenciado e natureza do vínculo" icon={Link2}>
              <div className="grid gap-5 sm:grid-cols-2">
                <CampoTexto
                  label="Credenciado"
                  value={item.credenciados?.nome ?? 'Sem vínculo informado'}
                  onChange={() => undefined}
                />
                <CampoTexto
                  label="Natureza do vínculo"
                  value={texto('tipo_vinculo_empregatorio')}
                  placeholder="Ex.: CLT, autônomo…"
                  onChange={(valor) => definir('tipo_vinculo_empregatorio', valor)}
                />
              </div>
            </FormSection>
          </>
        ) : (
          <>
            <FormSection
              title="Identificação do veículo"
              subtitle="Dados de circulação e registro"
              icon={Bus}
            >
              <div className="grid gap-5 sm:grid-cols-3">
                <CampoTexto
                  label="Placa"
                  value={mascaraPlaca(texto('placa'))}
                  mono
                  onChange={(valor) =>
                    definir('placa', valor.replace(/[^a-zA-Z0-9]/g, '').toUpperCase())
                  }
                />
                <CampoTexto
                  label="CRM"
                  value={texto('crm')}
                  mono
                  onChange={(valor) => definir('crm', valor)}
                />
                <div className="space-y-1">
                  <Label>Situação</Label>
                  <Select
                    value={texto('status') || 'ATIVO'}
                    onValueChange={(valor) => definir('status', valor)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OPCOES_DE_SITUACAO_DA_PESSOA.map((opcao) => (
                        <SelectItem key={opcao.value} value={opcao.value}>
                          {opcao.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <CampoTexto
                  label="Marca"
                  value={texto('marca')}
                  onChange={(valor) => definir('marca', valor)}
                />
                <CampoTexto
                  label="Modelo"
                  value={texto('modelo')}
                  onChange={(valor) => definir('modelo', valor)}
                />
                <CampoTexto
                  label="Cor"
                  value={texto('cor')}
                  onChange={(valor) => definir('cor', valor)}
                />
                <CampoTexto
                  label="Ano de fabricação"
                  type="number"
                  value={texto('ano_fabricacao')}
                  onChange={(valor) => definir('ano_fabricacao', valor)}
                />
                <CampoTexto
                  label="Ano do modelo"
                  type="number"
                  value={texto('ano_modelo')}
                  onChange={(valor) => definir('ano_modelo', valor)}
                />
                <CampoTexto
                  label="Tipo de combustível"
                  value={texto('tipo_combustivel')}
                  onChange={(valor) => definir('tipo_combustivel', valor)}
                />
                <CampoTexto
                  label="Chassi"
                  value={texto('chassi')}
                  mono
                  onChange={(valor) => definir('chassi', valor)}
                />
                <CampoTexto
                  label="RENAVAM"
                  value={texto('renavam')}
                  mono
                  onChange={(valor) => definir('renavam', valor)}
                />
              </div>
            </FormSection>
            <FormSection
              title="Capacidades e acessibilidade"
              subtitle="Lotação autorizada e características do veículo"
              icon={Accessibility}
            >
              <div className="grid gap-5 sm:grid-cols-4">
                <CampoTexto
                  label="Capacidade DETRAN"
                  type="number"
                  value={texto('capacidade_detran')}
                  onChange={(valor) => definir('capacidade_detran', valor)}
                />
                <CampoTexto
                  label="Capacidade TEG"
                  type="number"
                  value={texto('capacidade_teg')}
                  onChange={(valor) => definir('capacidade_teg', valor)}
                />
                <CampoTexto
                  label="Capacidade creche"
                  type="number"
                  value={texto('capacidade_creche')}
                  onChange={(valor) => definir('capacidade_creche', valor)}
                />
                <CampoTexto
                  label="Ancoragens acessíveis"
                  type="number"
                  value={texto('capacidade_acessivel')}
                  onChange={(valor) => definir('capacidade_acessivel', valor)}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <CampoBooleano
                  label="Veículo acessível"
                  checked={booleano('acessivel')}
                  onChange={(valor) => definir('acessivel', valor)}
                />
                <CampoTexto
                  label="Capacidade total"
                  type="number"
                  value={texto('capacidade_total')}
                  onChange={(valor) => definir('capacidade_total', valor)}
                />
              </div>
            </FormSection>
            <FormSection title="Documentação" subtitle="Validades regulatórias" icon={ShieldCheck}>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                <CamposData
                  label="CRLV"
                  value={texto('crlv_validade')}
                  onChange={(valor) => definir('crlv_validade', valor)}
                />
                <CamposData
                  label="CRM municipal"
                  value={texto('crm_pmsp_validade')}
                  onChange={(valor) => definir('crm_pmsp_validade', valor)}
                />
                <CamposData
                  label="Vistoria"
                  value={texto('vistoria_validade')}
                  onChange={(valor) => definir('vistoria_validade', valor)}
                />
                <CamposData
                  label="Tacógrafo"
                  value={texto('tacografo_validade')}
                  onChange={(valor) => definir('tacografo_validade', valor)}
                />
                <CamposData
                  label="Seguro"
                  value={texto('seguro_validade')}
                  onChange={(valor) => definir('seguro_validade', valor)}
                />
                <CampoTexto
                  label="Apólice de seguro"
                  value={texto('seguro_apolice')}
                  onChange={(valor) => definir('seguro_apolice', valor)}
                />
              </div>
            </FormSection>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">
                  A checagem de placa duplicada não é executada: a API original não possui uma rota
                  segura para consulta de duplicidade. A decisão desta rodada é manter o bloqueio
                  documentado, sem criar uma verificação mockada que pareça definitiva.
                </p>
              </CardContent>
            </Card>
          </>
        )}

        <FormSection title="Observações" icon={MessageSquare}>
          <Textarea
            value={texto('observacoes')}
            rows={4}
            maxLength={1000}
            placeholder="Informações adicionais…"
            onChange={(event) => definir('observacoes', event.target.value)}
          />
        </FormSection>

        {enviado && (
          <p className="text-sm text-destructive">
            Preencha os campos obrigatórios para continuar.
          </p>
        )}
        <div className="flex flex-col justify-end gap-3 border-t border-border pt-4 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/operacao/${tipo}/${item.slug}`)}
          >
            Cancelar
          </Button>
          <Button type="submit">
            <Save className="mr-2 h-4 w-4" /> Salvar alterações
          </Button>
        </div>
      </form>
      <p className="sr-only">Listagem relacionada: {lista}</p>
    </div>
  );
}

export default CadastroEdicaoPage;
